import React, { useState } from 'react';
import { JobPosting, User, ApplicationSubmission } from '../types';
import { api } from '../services/api';
import { X, Send, Link as LinkIcon, FileText, CheckCircle2, ShieldAlert } from 'lucide-react';

interface ApplicationModalProps {
  job: JobPosting | null;
  user: User | null;
  onClose: () => void;
  onSuccess: () => void;
  onRequireAuth: () => void;
}

export const ApplicationModal: React.FC<ApplicationModalProps> = ({
  job,
  user,
  onClose,
  onSuccess,
  onRequireAuth,
}) => {
  const [phone, setPhone] = useState('');
  const [discordTag, setDiscordTag] = useState(user?.discord_tag || '');
  const [minecraftIgn, setMinecraftIgn] = useState(user?.minecraft_ign || '');
  const [resumeUrl, setResumeUrl] = useState('');
  const [portfolioUrl, setPortfolioUrl] = useState('');
  const [coverLetter, setCoverLetter] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  if (!job) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) {
      onRequireAuth();
      return;
    }

    setError('');
    setLoading(true);

    try {
      const submission: ApplicationSubmission = {
        job_id: job.id,
        phone,
        discord_tag: discordTag,
        minecraft_ign: minecraftIgn,
        resume_url: resumeUrl,
        portfolio_url: portfolioUrl,
        cover_letter: coverLetter,
      };

      await api.submitApplication(submission);
      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to submit application');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" style={{ maxWidth: '640px' }} onClick={(e) => e.stopPropagation()}>
        <button className="modal-close-btn" onClick={onClose}>
          <X size={18} />
        </button>

        <div style={{ marginBottom: '24px' }}>
          <span style={{ fontSize: '0.8rem', color: 'var(--primary-cyan)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Application Form
          </span>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 800, marginTop: '4px' }}>
            Applying for: <span className="gradient-text">{job.title}</span>
          </h2>
          <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginTop: '4px' }}>
            {job.department} Department • {job.type} • {job.location}
          </p>
        </div>

        {!user && (
          <div
            style={{
              background: 'rgba(0, 242, 255, 0.08)',
              border: '1px solid var(--border-glow)',
              padding: '16px',
              borderRadius: 'var(--radius-md)',
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <div>
              <p style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--primary-cyan)' }}>
                Sign in to submit application
              </p>
              <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                An applicant account lets you track your application status live.
              </p>
            </div>
            <button className="btn-primary" style={{ padding: '8px 16px', fontSize: '0.85rem' }} onClick={onRequireAuth}>
              Sign In
            </button>
          </div>
        )}

        {error && (
          <div
            style={{
              background: 'rgba(244, 63, 94, 0.1)',
              border: '1px solid rgba(244, 63, 94, 0.3)',
              color: 'var(--accent-rose)',
              padding: '10px 14px',
              borderRadius: 'var(--radius-md)',
              fontSize: '0.85rem',
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <ShieldAlert size={16} />
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Applicant Name</label>
              <input type="text" className="form-input" value={user?.name || ''} disabled placeholder="Sign in required" />
            </div>
            <div className="form-group">
              <label className="form-label">Email Address</label>
              <input type="email" className="form-input" value={user?.email || ''} disabled placeholder="Sign in required" />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Discord Tag / Username</label>
              <input
                type="text"
                className="form-input"
                placeholder="e.g. Alex#1337 or alex_dev"
                value={discordTag}
                onChange={(e) => setDiscordTag(e.target.value)}
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">Minecraft IGN</label>
              <input
                type="text"
                className="form-input"
                placeholder="e.g. VanceDev"
                value={minecraftIgn}
                onChange={(e) => setMinecraftIgn(e.target.value)}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Resume / LinkedIn URL</label>
              <input
                type="url"
                className="form-input"
                placeholder="https://linkedin.com/in/yourname"
                value={resumeUrl}
                onChange={(e) => setResumeUrl(e.target.value)}
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">GitHub / Portfolio URL</label>
              <input
                type="url"
                className="form-input"
                placeholder="https://github.com/yourusername"
                value={portfolioUrl}
                onChange={(e) => setPortfolioUrl(e.target.value)}
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Cover Letter / Why SansCraft?</label>
            <textarea
              className="form-textarea"
              placeholder="Tell us about your background, relevant projects, plugin development experience, or passion for Minecraft network management..."
              value={coverLetter}
              onChange={(e) => setCoverLetter(e.target.value)}
              required
            />
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '24px' }}>
            <button type="button" className="btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={loading || !user}>
              <Send size={16} />
              {loading ? 'Submitting...' : 'Submit Application'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
